import React, { useRef, useState } from 'react'
import { UploadCloud, X } from 'lucide-react'
import { Button } from '@iws/admin-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@iws/admin-ui/dialog'
import { cn } from '../lib/utils'
import { Spinner } from '@iws/admin-ui/spinner'

interface UploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (file: File) => Promise<void>
  disabled?: boolean
}

export const UploadDialog = ({ open, onOpenChange, onUpload, disabled }: UploadDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const handleFiles = (files: FileList | null) => {
    if (!files) return
    setPendingFiles(Array.from(files))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleUpload = async () => {
    setIsUploading(true)
    try {
      for (const file of pendingFiles) {
        await onUpload(file)
        await new Promise((resolve) => setTimeout(resolve, 200))
      }
      setPendingFiles([])
      onOpenChange(false)
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      setPendingFiles([])
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Upload files</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {pendingFiles.length === 0 ? (
            <div
              className={cn(
                'cursor-pointer rounded-lg border border-dashed p-8 text-center transition-colors',
                isDragging
                  ? 'border-ink bg-subdued'
                  : 'border-input bg-card hover:bg-subdued',
              )}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <UploadCloud className="mx-auto mb-3 size-6 text-muted-foreground" />
              <span className="inline-flex h-7 items-center rounded-full bg-fill px-3 text-xs font-medium text-ink">Add files</span>
              <p className="mt-2 text-sm text-text-2">or drop images, videos and documents to upload</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
          ) : (
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {pendingFiles.map((file, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-border-subtle px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm text-ink">{file.name}</p>
                    <p className="text-xs tabular-nums text-text-2">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  {!isUploading && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove file"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <X />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={pendingFiles.length === 0 || isUploading || disabled}
          >
            {isUploading && <Spinner />}
            Upload {pendingFiles.length > 0 && `(${pendingFiles.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
